import type { QueryParams } from "@common/types/api";
import { camelizeKeys, decamelizeKeys } from "humps";
import { mergeDeep } from "remeda";

import { ApiError } from "helpers/ApiError";
import buildUrlWithParams from "helpers/buildUrlWithParams";

const defaultOptions = {
  credentials: "include"
} as const;

type FetchOptions = {
  queryParams?: QueryParams;
} & RequestInit;

export const jsonFetch = async <T>(
  url: string,
  {
    headers = { Accept: "application/json" },
    queryParams = {},
    ...options
  }: FetchOptions = {}
): Promise<T> => {
  const urlWithParams = buildUrlWithParams(url, queryParams);

  const response = await fetch(urlWithParams, {
    ...defaultOptions,
    headers,
    ...options
  });

  if (!response.ok) {
    throw new ApiError(response.status, await response.json().catch(() => undefined));
  }

  const json = await response.json().catch(() => ({}));

  return camelizeKeys(json) as T;
};

export const jsonMutate = <Response, Params = Record<string, unknown>>(
  url: string,
  params: Params,
  options = {}
) =>
  fetch(
    url,
    mergeDeep(mergeDeep(defaultOptions, options), {
      body: JSON.stringify(decamelizeKeys(params)),
      headers: {
        "Content-Type": "application/json"
      }
    })
  )
    .then(async (response) => {
      if (!response.ok) {
        throw new ApiError(response.status, await response.json().catch(() => undefined));
      }

      return response.json().catch(() => ({ status: response.status }));
    })
    .then((json) => camelizeKeys<Response>(json));
